import { useEffect, useState } from "react"
import { Info, X } from "lucide-react"
import type { TFunction } from "i18next"
import { useTranslation } from "react-i18next"
import { Button } from "@/components/ui/button"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { extractVhostFromUser } from "@/jidUtils"
import { entryPath, PLANNER_TAB, TAB_PARAM } from "@/lib/appEntryLink"
import {
  dismissAccountNotice,
  getRememberedEmail,
  isAccountNoticeDismissed,
} from "@/lib/rememberedAccount"
import { LEGACY_LANGUAGE } from "@/lib/i18n/language"

type NoticeKind = "mode_chip" | "explainer" | "returning_account"

// The public page has no account to take a language from. Legacy mode, which
// every anonymous visitor gets, keeps following the browser as before: Polish
// (from the legacy bundle) for a Polish browser, English otherwise. A visitor
// who chose a language in the app gets that language.
function usePublicT(): TFunction {
  const { t, i18n } = useTranslation()
  if (
    i18n.language === LEGACY_LANGUAGE &&
    !navigator.language.toLowerCase().startsWith("pl")
  ) {
    return i18n.getFixedT("en")
  }
  return t
}

// A full page load, not a router push: the public page holds an anonymous
// XMPP connection that the sign-in route would otherwise take for a session.
function signIn(kind: NoticeKind) {
  window.analytics?.track("PublicPlanner_SignInClicked", { from: kind })
  const search = new URLSearchParams(window.location.search)
  search.set(TAB_PARAM, PLANNER_TAB)
  window.location.assign(entryPath("/signin", `?${search}`))
}

export function PublicModeChip() {
  const t = usePublicT()
  return (
    <Popover
      onOpenChange={(open) => {
        if (open) window.analytics?.track("PublicPlanner_ExplainerOpened")
      }}
    >
      <div
        data-testid="public-mode-chip"
        className="mt-2 flex w-max max-w-full items-center gap-1.5 rounded-full border border-gray-200 bg-gray-100 px-2.5 py-1 text-xs text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300"
      >
        <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-gray-400" />
        <span className="truncate">{t("planOrder.publicMode.mode")}</span>
        <PopoverTrigger
          aria-label={t("planOrder.publicMode.explainerLabel")}
          className="text-gray-500 hover:text-gray-900 dark:hover:text-gray-100"
        >
          <Info className="h-3.5 w-3.5" />
        </PopoverTrigger>
        <span aria-hidden>·</span>
        <button
          type="button"
          className="font-semibold text-blue-600 hover:underline dark:text-blue-400"
          onClick={() => signIn("mode_chip")}
        >
          {t("planOrder.publicMode.signIn")}
        </button>
      </div>
      <PopoverContent
        align="start"
        className="z-[1100] w-72 text-sm"
        data-testid="public-mode-explainer"
      >
        <p className="font-semibold">
          {t("planOrder.publicMode.explainerLabel")}
        </p>
        <p className="mt-1 text-gray-600 dark:text-gray-400">
          {t("planOrder.publicMode.explainer")}
        </p>
        <button
          type="button"
          className="mt-2 font-semibold text-blue-600 hover:underline dark:text-blue-400"
          onClick={() => signIn("explainer")}
        >
          {t("planOrder.publicMode.signInToPlan")}
        </button>
      </PopoverContent>
    </Popover>
  )
}

export function ReturningAccountBar() {
  const t = usePublicT()
  const [email, setEmail] = useState(() =>
    isAccountNoticeDismissed() ? null : getRememberedEmail(),
  )

  useEffect(() => {
    if (!email) return
    window.analytics?.track("PublicPlanner_ReturningAccountShown", {
      vhost: extractVhostFromUser(email),
    })
  }, [email])

  if (!email) return null
  const vhost = extractVhostFromUser(email)

  return (
    <div
      data-testid="returning-account-bar"
      className="mb-2 flex flex-col gap-2 rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm dark:border-blue-900 dark:bg-blue-950"
    >
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1 leading-snug">
          <p className="font-semibold">
            {t("planOrder.publicMode.notSignedIn", { vhost })}
          </p>
          <p className="text-xs text-gray-600 dark:text-gray-400">
            {t("planOrder.publicMode.ignores")}
          </p>
        </div>
        <button
          type="button"
          aria-label={t("planOrder.publicMode.dismiss")}
          className="shrink-0 text-gray-500 hover:text-gray-900 dark:hover:text-gray-100"
          onClick={() => {
            dismissAccountNotice()
            setEmail(null)
            window.analytics?.track("PublicPlanner_ReturningAccountDismissed")
          }}
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <Button
        size="sm"
        className="w-max max-w-full truncate bg-blue-600 text-white hover:bg-blue-700"
        onClick={() => signIn("returning_account")}
      >
        {t("planOrder.publicMode.continueAs", { email })}
      </Button>
    </div>
  )
}
